'use strict';

const Tile = require('./Tile.class');

class Game {
  constructor(initialState) {
    this.size = 4;
    this.score = 0;
    this.status = 'idle';

    this.board = Array(this.size)
      .fill(null)
      .map(() => Array(this.size).fill(null));

    if (initialState) {
      this.score = initialState.score;
      this.status = initialState.status;

      initialState.board.forEach((row, y) => {
        row.forEach((value, x) => {
          if (value !== 0) {
            this.board[y][x] = new Tile(
              {
                x,
                y,
              },
              value,
            );
          }
        });
      });
    } else {
      this.addRandomTile();
      this.addRandomTile();
    }
  }

  prepareTiles() {
    this.board.flat().forEach((tile) => {
      if (tile) {
        tile.mergedFrom = null;
        tile.savePosition();
      }
    });
  }

  syncTilePositions() {
    this.board.forEach((row, y) => {
      row.forEach((tile, x) => {
        if (tile) {
          tile.updatePosition({
            x,
            y,
          });
        }
      });
    });
  }

  shiftLeft() {
    let moved = false;

    for (let y = 0; y < this.size; y++) {
      let row = this.board[y].filter((tile) => tile !== null);

      for (let i = 0; i < row.length - 1; i++) {
        if (row[i] === null) {
          continue;
        }

        if (row[i].value === row[i + 1].value) {
          const tile1 = row[i];
          const tile2 = row[i + 1];
          const val = tile1.value * 2;

          this.score += val;

          if (val === 2048) {
            this.status = 'won';
          }

          const merged = new Tile(
            {
              x: -1,
              y: -1,
            },
            val,
          );

          merged.mergedFrom = [tile1, tile2];

          row[i] = merged;
          row[i + 1] = null;
        }
      }

      row = row.filter((tile) => tile !== null);

      while (row.length < this.size) {
        row.push(null);
      }

      if (this.arraysEqual(this.board[y], row) === false) {
        moved = true;
        this.board[y] = row;
      }
    }

    return moved;
  }

  afterMove() {
    this.syncTilePositions();
    this.addRandomTile();

    if (this.canMove() === false) {
      this.status = 'lost';
    }
  }

  moveLeft() {
    this.prepareTiles();

    const moved = this.shiftLeft();

    if (moved) {
      this.afterMove();
    }
  }

  moveRight() {
    this.prepareTiles();
    this.reverseRows();

    const moved = this.shiftLeft();

    this.reverseRows();

    if (moved) {
      this.afterMove();
    }
  }

  moveUp() {
    this.prepareTiles();
    this.transpose();

    const moved = this.shiftLeft();

    this.transpose();

    if (moved) {
      this.afterMove();
    }
  }

  moveDown() {
    this.prepareTiles();
    this.transpose();
    this.reverseRows();

    const moved = this.shiftLeft();

    this.reverseRows();
    this.transpose();

    if (moved) {
      this.afterMove();
    }
  }

  getScore() {
    return this.score;
  }

  getState() {
    return {
      board: this.board.map((row) =>
        row.map((tile) => (tile ? tile.value : 0)),
      ),
      score: this.score,
      status: this.status,
      tiles: this.board
        .flat()
        .filter((tile) => tile !== null)
        .map((tile) => tile.serialize()),
    };
  }

  getStatus() {
    return this.status;
  }

  start() {
    this.restart();
  }

  restart() {
    this.board = Array(this.size)
      .fill(null)
      .map(() => Array(this.size).fill(null));
    this.score = 0;
    this.status = 'playing';
    this.addRandomTile();
    this.addRandomTile();
  }

  addRandomTile() {
    const emptyCells = [];

    for (let y = 0; y < this.size; y++) {
      for (let x = 0; x < this.size; x++) {
        if (this.board[y][x] === null) {
          emptyCells.push({
            x,
            y,
          });
        }
      }
    }

    if (emptyCells.length === 0) {
      return;
    }

    const { x: randX, y: randY } =
      emptyCells[Math.floor(Math.random() * emptyCells.length)];

    this.board[randY][randX] = new Tile(
      {
        x: randX,
        y: randY,
      },
      Math.random() < 0.1 ? 4 : 2,
    );
  }

  arraysEqual(a, b) {
    return a.length === b.length && a.every((val, index) => val === b[index]);
  }

  canMove() {
    for (let y = 0; y < this.size; y++) {
      for (let x = 0; x < this.size; x++) {
        const val = this.board[y][x];

        if (val === null) {
          return true;
        }

        if (x < this.size - 1) {
          const right = this.board[y][x + 1];

          if (right && val.value === right.value) {
            return true;
          }
        }

        if (y < this.size - 1) {
          const down = this.board[y + 1][x];

          if (down && val.value === down.value) {
            return true;
          }
        }
      }
    }

    return false;
  }

  reverseRows() {
    for (const row of this.board) {
      row.reverse();
    }
  }

  transpose() {
    const newBoard = Array(this.size)
      .fill(null)
      .map(() => Array(this.size).fill(null));

    for (let y = 0; y < this.size; y++) {
      for (let x = 0; x < this.size; x++) {
        newBoard[x][y] = this.board[y][x];
      }
    }

    this.board = newBoard;
  }
}

module.exports = Game;
